import { useEffect } from 'react';
import settings from '@/content/settings.json';

const HEX_RE = /^#[0-9a-f]{6}$/i;

/** Mescola `hex` verso `toward` (altro colore #rrggbb) di `amount` (0-1). */
function mix(hex, toward, amount) {
  const a = hex.replace('#', '');
  const b = toward.replace('#', '');
  const ch = (s, i) => parseInt(s.slice(i, i + 2), 16);
  const out = [0, 2, 4].map((i) => Math.round(ch(a, i) + (ch(b, i) - ch(a, i)) * amount));
  return '#' + out.map((v) => v.toString(16).padStart(2, '0')).join('');
}

/**
 * Applica il colore di fondo sitewide scelto in Impostazioni → Aspetto
 * (default = colore del tema, nessuna modifica). A differenza di
 * TypographySettings (il font non varia per modo) qui il valore dipende
 * da `data-mode` — che ThemeToggle.jsx cambia via setAttribute diretto sul
 * <html>, fuori da React — quindi si osserva l'attributo con un
 * MutationObserver invece di leggerlo una volta sola al mount.
 *
 * Deriva anche `--color-bg-subtle`/`--color-border` dal nuovo sfondo
 * (mix verso nero in light, verso bianco in dark) invece di lasciarli al
 * valore del tema: molti blocchi (es. GoogleReviewsBlock) usano una card
 * `bg-theme-bg` + `border-theme-border` per distinguersi dalla pagina —
 * se solo `--color-bg` cambia, il bordo (calibrato sul colore originale
 * del tema) perde contrasto e la card si confonde con lo sfondo.
 */
export function BackgroundSettings() {
  useEffect(() => {
    const root = document.documentElement;
    const { bgLight = '', bgDark = '' } = settings.appearance || {};
    if (!bgLight && !bgDark) return undefined;

    function apply() {
      const mode = root.getAttribute('data-mode') || 'light';
      const bg = mode === 'dark' ? bgDark : bgLight;

      if (!bg || !HEX_RE.test(bg)) {
        root.style.removeProperty('--color-bg');
        root.style.removeProperty('--color-bg-subtle');
        root.style.removeProperty('--color-border');
        return;
      }

      const toward = mode === 'dark' ? '#ffffff' : '#000000';
      root.style.setProperty('--color-bg', bg);
      root.style.setProperty('--color-bg-subtle', mix(bg, toward, mode === 'dark' ? 0.08 : 0.05));
      root.style.setProperty('--color-border', mix(bg, toward, mode === 'dark' ? 0.18 : 0.12));
    }

    apply();
    const observer = new MutationObserver(apply);
    observer.observe(root, { attributes: true, attributeFilter: ['data-mode'] });
    return () => observer.disconnect();
  }, []);
  return null;
}
